import { SegmentableCheckoutCharacteristic } from "@redotech/redo-conversion-core-common/checkouts/checkout-tree";
import {
  RcoSegmentStep,
  rcoSegmentStepSchema,
} from "@redotech/redo-flows-common/advanced-flow-db-parser";
import { StepType } from "@redotech/redo-flows-core-schema-common/common";
import { assertNever } from "@redotech/util/type";
import { memo } from "react";
import { PartialWithRequiredType } from "..";
import {
  AddressPropertiesInspectorContent,
  EditableAddressPropertiesInspectorContent,
} from "./address-properties/address-properties-inspector";
import {
  CartValueInspectorContent,
  EditableCartValueInspectorContent,
} from "./cart-value/cart-value-inspector";
import {
  CheckoutProbabilityInspectorContent,
  EditableCheckoutProbabilityInspectorContent,
} from "./checkout-probability/checkout-probability-inspector";
import {
  CustomerOrderCountInspectorContent,
  EditableCustomerOrderCountInspectorContent,
} from "./customer-order-count/customer-order-count-inspector";
import {
  CustomerReturnRateInspectorContent,
  EditableCustomerReturnRateInspectorContent,
} from "./customer-return-rate/customer-return-rate-inspector";
import {
  CustomerTagsInspectorContent,
  EditableCustomerTagsInspectorContent,
} from "./customer-tags/customer-tags-inspector";
import {
  DatetimeRangeInspectorContent,
  EditableDatetimeRangeInspectorContent,
} from "./datetime-range/datetime-range-inspector";
import {
  DeliveryDistanceMilesInspectorContent,
  EditableDeliveryDistanceMilesInspectorContent,
} from "./delivery-distance-miles/delivery-distance-miles-inspector";
import {
  DeliveryLocationInspectorContent,
  EditableDeliveryLocationInspectorContent,
} from "./delivery-location/delivery-location-inspector";
import {
  DeliveryLocationRulesInspectorContent,
  EditableDeliveryLocationRulesInspectorContent,
} from "./delivery-location/delivery-location-rules-inspector";
import {
  EditableHistoricalOrderValueInspectorContent,
  HistoricalOrderValueInspectorContent,
} from "./historical-order-value/historical-order-value-inspector";
import {
  EditableItemQuantityInspectorContent,
  ItemQuantityInspectorContent,
} from "./item-quantity/item-quantity-inspector";
import {
  EditableLineItemPropertiesInspectorContent,
  LineItemPropertiesInspectorContent,
} from "./line-item-properties/line-item-properties-inspector";
import {
  EditableOriginLocationInspectorContent,
  OriginLocationInspectorContent,
} from "./origin-location/origin-location-inspector";
import {
  EditableProductCollectionsInspectorContent,
  ProductCollectionsInspectorContent,
} from "./product-collections/product-collections-inspector";
import {
  EditableProductMetafieldsInspectorContent,
  ProductMetafieldsInspectorContent,
} from "./product-metafields/product-metafields-inspector";
import {
  EditableProductSkuInspectorContent,
  ProductSkuInspectorContent,
} from "./product-sku/product-sku-inspector";
import {
  EditableProductTagsInspectorContent,
  ProductTagsInspectorContent,
} from "./product-tags/product-tags-inspector";
import {
  EditableProductVendorInspectorContent,
  ProductVendorInspectorContent,
} from "./product-vendor/product-vendor-inspector";
import {
  EditableShipmentWeightInspectorContent,
  ShipmentWeightInspectorContent,
} from "./shipment-weight/shipment-weight-inspector";
import { StaticConditionInspectorContent } from "./static-condition-inspector";
import {
  EditableTimeOfDayInspectorContent,
  TimeOfDayInspectorContent,
} from "./time-of-day/time-of-day-inspector";

export const rcoSegmentStepSchemaOptionalNextIds = rcoSegmentStepSchema.partial(
  { matchingNextId: true, nonMatchingNextId: true },
);

/**
 * Renders the editor for a single (binary) segment characteristic. Extracted so
 * both the normal segment inspector and the compound-segment leaf builder share
 * one dispatch over per-type editors. Each per-type editor reads and writes only
 * `step.segment`, so callers may pass a synthetic single-segment step.
 */
export const SegmentTypeInspectorContent = memo(
  function SegmentTypeInspectorContent({
    step,
    setStep,
    readonly,
  }: {
    step: PartialWithRequiredType<RcoSegmentStep>;
    setStep: (step: PartialWithRequiredType<RcoSegmentStep>) => void;
    readonly: boolean | undefined;
  }) {
    if (
      step.type !== StepType.RCO_SEGMENT ||
      !step.segment?.segmentableCharacteristic
    ) {
      return null;
    }

    switch (step.segment.segmentableCharacteristic) {
      case SegmentableCheckoutCharacteristic.ORDER_COUNT:
        return readonly ? (
          <CustomerOrderCountInspectorContent
            step={rcoSegmentStepSchema.parse(step)}
          />
        ) : (
          <EditableCustomerOrderCountInspectorContent
            setStep={setStep}
            step={rcoSegmentStepSchemaOptionalNextIds.parse(step)}
          />
        );
      case SegmentableCheckoutCharacteristic.HISTORICAL_ORDER_VALUE:
        return readonly ? (
          <HistoricalOrderValueInspectorContent
            step={rcoSegmentStepSchema.parse(step)}
          />
        ) : (
          <EditableHistoricalOrderValueInspectorContent
            setStep={setStep}
            step={rcoSegmentStepSchemaOptionalNextIds.parse(step)}
          />
        );
      case SegmentableCheckoutCharacteristic.DELIVERY_DISTANCE_MILES:
        return readonly ? (
          <DeliveryDistanceMilesInspectorContent
            step={rcoSegmentStepSchema.parse(step)}
          />
        ) : (
          <EditableDeliveryDistanceMilesInspectorContent
            setStep={setStep}
            step={rcoSegmentStepSchemaOptionalNextIds.parse(step)}
          />
        );
      case SegmentableCheckoutCharacteristic.DELIVERY_LOCATION:
        return readonly ? (
          <DeliveryLocationInspectorContent
            step={rcoSegmentStepSchema.parse(step)}
          />
        ) : (
          <EditableDeliveryLocationInspectorContent
            setStep={setStep}
            step={rcoSegmentStepSchemaOptionalNextIds.parse(step)}
          />
        );
      case SegmentableCheckoutCharacteristic.CROSS_BORDER_SHIPMENT:
        return (
          <StaticConditionInspectorContent
            characteristic={
              SegmentableCheckoutCharacteristic.CROSS_BORDER_SHIPMENT
            }
          />
        );
      case SegmentableCheckoutCharacteristic.PO_BOX_SHIPMENT:
        return (
          <StaticConditionInspectorContent
            characteristic={SegmentableCheckoutCharacteristic.PO_BOX_SHIPMENT}
          />
        );
      case SegmentableCheckoutCharacteristic.LCG_ELIGIBLE_SHIPMENT:
        return (
          <StaticConditionInspectorContent
            characteristic={
              SegmentableCheckoutCharacteristic.LCG_ELIGIBLE_SHIPMENT
            }
          />
        );
      case SegmentableCheckoutCharacteristic.CUSTOMER_TAGS:
        return readonly ? (
          <CustomerTagsInspectorContent
            step={rcoSegmentStepSchema.parse(step)}
          />
        ) : (
          <EditableCustomerTagsInspectorContent
            setStep={setStep}
            step={rcoSegmentStepSchemaOptionalNextIds.parse(step)}
          />
        );
      case SegmentableCheckoutCharacteristic.PRODUCT_TAGS:
        return readonly ? (
          <ProductTagsInspectorContent
            step={rcoSegmentStepSchema.parse(step)}
          />
        ) : (
          <EditableProductTagsInspectorContent
            setStep={setStep}
            step={rcoSegmentStepSchemaOptionalNextIds.parse(step)}
          />
        );
      case SegmentableCheckoutCharacteristic.PRODUCT_VENDOR:
        return readonly ? (
          <ProductVendorInspectorContent
            step={rcoSegmentStepSchema.parse(step)}
          />
        ) : (
          <EditableProductVendorInspectorContent
            setStep={setStep}
            step={rcoSegmentStepSchemaOptionalNextIds.parse(step)}
          />
        );
      case SegmentableCheckoutCharacteristic.PRODUCT_SKU:
        return readonly ? (
          <ProductSkuInspectorContent step={rcoSegmentStepSchema.parse(step)} />
        ) : (
          <EditableProductSkuInspectorContent
            setStep={setStep}
            step={rcoSegmentStepSchemaOptionalNextIds.parse(step)}
          />
        );
      case SegmentableCheckoutCharacteristic.ITEM_QUANTITY:
        return readonly ? (
          <ItemQuantityInspectorContent
            step={rcoSegmentStepSchema.parse(step)}
          />
        ) : (
          <EditableItemQuantityInspectorContent
            setStep={setStep}
            step={rcoSegmentStepSchemaOptionalNextIds.parse(step)}
          />
        );
      case SegmentableCheckoutCharacteristic.ORIGIN_LOCATION:
        return readonly ? (
          <OriginLocationInspectorContent
            step={rcoSegmentStepSchema.parse(step)}
          />
        ) : (
          <EditableOriginLocationInspectorContent
            setStep={setStep}
            step={rcoSegmentStepSchemaOptionalNextIds.parse(step)}
          />
        );
      case SegmentableCheckoutCharacteristic.CUSTOMER_RETURN_RATE:
        return readonly ? (
          <CustomerReturnRateInspectorContent
            step={rcoSegmentStepSchema.parse(step)}
          />
        ) : (
          <EditableCustomerReturnRateInspectorContent
            setStep={setStep}
            step={rcoSegmentStepSchemaOptionalNextIds.parse(step)}
          />
        );
      case SegmentableCheckoutCharacteristic.TIME_OF_DAY:
        return readonly ? (
          <TimeOfDayInspectorContent step={rcoSegmentStepSchema.parse(step)} />
        ) : (
          <EditableTimeOfDayInspectorContent
            setStep={setStep}
            step={rcoSegmentStepSchemaOptionalNextIds.parse(step)}
          />
        );
      case SegmentableCheckoutCharacteristic.DATETIME_RANGE:
        return readonly ? (
          <DatetimeRangeInspectorContent
            step={rcoSegmentStepSchema.parse(step)}
          />
        ) : (
          <EditableDatetimeRangeInspectorContent
            setStep={setStep}
            step={rcoSegmentStepSchemaOptionalNextIds.parse(step)}
          />
        );
      case SegmentableCheckoutCharacteristic.CHECKOUT_PROBABILITY:
        return readonly ? (
          <CheckoutProbabilityInspectorContent
            step={rcoSegmentStepSchema.parse(step)}
          />
        ) : (
          <EditableCheckoutProbabilityInspectorContent
            setStep={setStep}
            step={rcoSegmentStepSchemaOptionalNextIds.parse(step)}
          />
        );
      case SegmentableCheckoutCharacteristic.PRODUCT_COLLECTIONS:
        return readonly ? (
          <ProductCollectionsInspectorContent
            step={rcoSegmentStepSchema.parse(step)}
          />
        ) : (
          <EditableProductCollectionsInspectorContent
            setStep={setStep}
            step={rcoSegmentStepSchemaOptionalNextIds.parse(step)}
          />
        );
      case SegmentableCheckoutCharacteristic.LINE_ITEM_PROPERTIES:
        return readonly ? (
          <LineItemPropertiesInspectorContent
            step={rcoSegmentStepSchema.parse(step)}
          />
        ) : (
          <EditableLineItemPropertiesInspectorContent
            setStep={setStep}
            step={rcoSegmentStepSchemaOptionalNextIds.parse(step)}
          />
        );
      case SegmentableCheckoutCharacteristic.PRODUCT_METAFIELDS:
        return readonly ? (
          <ProductMetafieldsInspectorContent
            step={rcoSegmentStepSchema.parse(step)}
          />
        ) : (
          <EditableProductMetafieldsInspectorContent
            setStep={setStep}
            step={rcoSegmentStepSchemaOptionalNextIds.parse(step)}
          />
        );
      case SegmentableCheckoutCharacteristic.CART_VALUE:
        return readonly ? (
          <CartValueInspectorContent step={rcoSegmentStepSchema.parse(step)} />
        ) : (
          <EditableCartValueInspectorContent
            setStep={setStep}
            step={rcoSegmentStepSchemaOptionalNextIds.parse(step)}
          />
        );
      case SegmentableCheckoutCharacteristic.SHIPMENT_WEIGHT:
        return readonly ? (
          <ShipmentWeightInspectorContent
            step={rcoSegmentStepSchema.parse(step)}
          />
        ) : (
          <EditableShipmentWeightInspectorContent
            setStep={setStep}
            step={rcoSegmentStepSchemaOptionalNextIds.parse(step)}
          />
        );
      case SegmentableCheckoutCharacteristic.ADDRESS_PROPERTIES:
        return readonly ? (
          <AddressPropertiesInspectorContent
            step={rcoSegmentStepSchema.parse(step)}
          />
        ) : (
          <EditableAddressPropertiesInspectorContent
            setStep={setStep}
            step={rcoSegmentStepSchemaOptionalNextIds.parse(step)}
          />
        );
      case SegmentableCheckoutCharacteristic.DELIVERY_LOCATION_RULES:
        return readonly ? (
          <DeliveryLocationRulesInspectorContent
            step={rcoSegmentStepSchema.parse(step)}
          />
        ) : (
          <EditableDeliveryLocationRulesInspectorContent
            setStep={setStep}
            step={rcoSegmentStepSchemaOptionalNextIds.parse(step)}
          />
        );
      // N-ary characteristics are not editable as a single binary segment.
      case SegmentableCheckoutCharacteristic.DELIVERY_LOCATION_V02262026:
      case SegmentableCheckoutCharacteristic.COMPOUND:
        return null;
      default:
        return assertNever(step.segment.segmentableCharacteristic);
    }
  },
);
